import { NavLink, Outlet, useOutletContext, useParams } from 'react-router-dom'

import * as api from 'api/api'
import { Project } from 'api/types'
import { ROLE_LABELS, formatDate } from 'lib/format'
import { useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'

import { useSetActiveProject } from './app-shell/context/ActiveProjectContext'
import { AppShell } from './app-shell/templates/AppShell'
import { Icon } from './Icon'
import { ErrorState, Spinner } from './ui'

export const AppLayout = () => (
  <AppShell>
    <Outlet />
  </AppShell>
)

export interface ProjectContextValue {
  project: Project
  isManager: boolean
  reloadProject: () => void
}

export const useProject = () => useOutletContext<ProjectContextValue>()

export const ProjectLayout = () => {
  const { projectId = '' } = useParams()
  const { data: project, error, loading, reload } = useAsync(() => api.fetchProject(projectId), [projectId])

  useSetActiveProject(project ?? null)

  if (loading && !project) return <Spinner />
  if (error || !project) return <ErrorState message={error ?? t('المشروع غير موجود', 'Project not found')} onRetry={reload} />

  const isManager = project.myRole === 'ADMIN' || project.myRole === 'PROJECT_MANAGER'
  const context: ProjectContextValue = { isManager, project, reloadProject: reload }
  const tabs = [
    ...(isManager ? [{ icon: 'home' as const, label: t('نظرة عامة', 'Overview'), to: `/projects/${project.id}/overview` }] : []),
    ...(isManager ? [{ icon: 'mapPin' as const, label: t('مناطق العمل', 'Work areas'), to: `/projects/${project.id}/work-areas` }] : []),
    ...(project.myRole === 'SITE_ENGINEER' ? [{ icon: 'grid' as const, label: t('اليوم', 'Today'), to: `/projects/${project.id}/today` }] : []),
    { end: true, icon: 'calendar' as const, label: t('فهرس التقارير', 'Report index'), to: `/projects/${project.id}` },
    ...(isManager
      ? [
          { icon: 'users' as const, label: t('الكوادر', 'Crew'), to: `/projects/${project.id}/crew` },
          { icon: 'truck' as const, label: t('المعدات', 'Equipment'), to: `/projects/${project.id}/equipment` },
          { icon: 'box' as const, label: t('المواد', 'Materials'), to: `/projects/${project.id}/materials` },
        ]
      : []),
    { icon: 'alert' as const, label: t('المشاكل', 'Issues'), to: `/projects/${project.id}/issues` },
    { icon: 'file' as const, label: t('المستندات', 'Documents'), to: `/projects/${project.id}/documents` },
    { icon: 'paperclip' as const, label: t('أرشيف المرفقات', 'Attachments'), to: `/projects/${project.id}/attachments` },
    { icon: 'chart' as const, label: t('الملخصات', 'Summaries'), to: `/projects/${project.id}/summaries` },
    ...(isManager ? [{ icon: 'settings' as const, label: t('الإعدادات', 'Settings'), to: `/projects/${project.id}/settings` }] : []),
  ]

  return (
    <div className="project">
      <div className="project__head">
        <div className="project__meta">
          {project.tenderNumber ? <span>{t(`عطاء رقم ${project.tenderNumber}`, `Tender no. ${project.tenderNumber}`)}</span> : null}
          <span>
            {formatDate(project.startDate)} – {formatDate(project.endDate)}
          </span>
          <span className="chip">{ROLE_LABELS[project.myRole]}</span>
        </div>
        <nav className="tabs" aria-label={t('أقسام المشروع', 'Project sections')}>
          {tabs.map((tab) => (
            <NavLink key={tab.to} to={tab.to} end={tab.end} className={({ isActive }) => (isActive ? 'tab is-active' : 'tab')}>
              <Icon name={tab.icon} />
              {tab.label}
            </NavLink>
          ))}
        </nav>
      </div>
      <Outlet context={context} />
    </div>
  )
}
